import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageChoice, ThemeChoice, UserMenu } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';

const LANGUAGES = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { name, logout } = useAuthStore();

  // Built inside the component so the labels re-resolve when the language changes; as a
  // module-level constant they would be captured once, in whatever language loaded first.
  const nav = [
    { to: '/', label: t('nav.dashboard', 'Dashboard'), end: true },
    { to: '/creatives', label: t('nav.creatives', 'Creatives'), end: false },
    { to: '/campaigns', label: t('nav.campaigns', 'Campaigns'), end: false },
    { to: '/account', label: t('nav.account', 'Your data'), end: false },
  ];
  const preferences = (
    <>
      <ThemeChoice labels={{
        label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'),
        dark: t('theme.dark', 'Dark'), system: t('theme.system', 'Auto'),
      }} />
      <LanguageChoice
        value={i18n.language}
        languages={LANGUAGES}
        onChange={(code) => i18n.changeLanguage(code)}
        label={t('language', 'Language')}
      />
    </>
  );

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-5xl items-center gap-6 px-6 py-3">
          <div className="flex items-center gap-2 font-bold text-brand-ink-strong">
            <span className="text-lg">⬡</span> easyDent{' '}
            <span className="font-normal text-subtle-ink">{t('portalName', 'Advertisers')}</span>
          </div>
          <nav className="hidden gap-1 text-sm sm:flex">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-1.5 font-medium ${isActive ? 'bg-brand-50 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
              <UserMenu
                name={name}
                items={[]}
                preferences={preferences}
                onLogout={() => { logout(); navigate('/login'); }}
                logoutLabel={t('signOut', 'Sign out')}
              />
            </div>
        </div>

        {/* Mobile: the nav gets its own scrollable row rather than a hamburger. With a handful of
            destinations, hiding them behind a tap costs more than it saves. */}
        <nav className="flex gap-1 overflow-x-auto border-t border-line-soft px-4 py-2 text-sm sm:hidden">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `shrink-0 rounded-lg px-3 py-1 font-medium ${isActive ? 'bg-brand-50 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-6">{children}</main>
    </div>
  );
};

export default Layout;
